import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

import ROUTES from '../../routes';
import api from './../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

const resolveImageUrl = (img, baseUrl) => {
    if (!img) return '';
    return img.startsWith('http') ? img : baseUrl + img;
};

function EditTestimonial() {
    const { slug } = useParams();
    const formRef = useRef(null);
    const navigate = useNavigate();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [username, setUsername] = useState('');
    const [designation, setDesignation] = useState('');

    const [img, setImg] = useState(null);
    const [imgPreview, setImgPreview] = useState(null);
    const [fetching, setFetching] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const buildFormData = () => {
        const data = new FormData();
        data.append('title', title);
        if (description) data.append('description', description);
        if (username) data.append('username', username);
        if (designation) data.append('designation', designation);
        if (img) data.append('img', img);
        return data;
    };

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            $(form)
                .find('.is-valid, .is-invalid')
                .removeClass('is-valid is-invalid');

            $(form)
                .find('label.error, .invalid-feedback')
                .remove();

            try {
                setSubmitting(true);
                const res = await api.put(
                    `/testimonials/${slug}`,
                    buildFormData(),
                    {
                        headers: {
                            "Content-Type": "multipart/form-data"
                        }
                    }
                );

                if (res.status === 200) {
                    showToast('success', res.data.message || 'Successfully updated', 'Success');
                    navigate(ROUTES.TESTIMONIALS);
                }
            } catch (err) {
                // Try plural if 404 occurs
                if (err?.response?.status === 404) {
                    try {
                        const res2 = await api.put(`/testimonials/${slug}`, buildFormData(), {
                            headers: { "Content-Type": "multipart/form-data" }
                        });
                        showToast('success', res2.data.message || 'Successfully updated', 'Success');
                        navigate(ROUTES.TESTIMONIALS);
                    } catch (err2) {
                        handleErrors(err2);
                    }
                } else {
                    handleErrors(err);
                }
            } finally {
                setSubmitting(false);
            }
        }
    };



    useEffect(() => {
        const fetchTestimonial = async () => {
            try {
                // It might be available at /testimonials/{slug} or /testimonial/{slug}
                // Try typical plural fetch single endpoint
                const res = await api.get(`/testimonials/${slug}`);
                if (res.status === 200) {
                    const data = res.data.testimonial || res.data;
                    setTitle(data.title || '');
                    setDescription(data.description || '');
                    setUsername(data.username || '');
                    setDesignation(data.designation || '');

                    if (data.img) {
                        setImgPreview(resolveImageUrl(data.img, res.data.base_url || ''));
                    }
                }
            } catch (err) {
                // Fallback to searching in list or using another endpoint format if typical fails
                if (err?.response?.status === 404) {
                    try {
                        const res2 = await api.get(`/testimonial/${slug}`);
                        const data = res2.data.testimonial || res2.data;
                        setTitle(data.title || '');
                        setDescription(data.description || '');
                        setUsername(data.username || '');
                        setDesignation(data.designation || '');

                        if (data.img) {
                            setImgPreview(resolveImageUrl(data.img, res2.data.base_url || ''));
                        }
                    } catch (err2) {
                        handleErrors(err2);
                    }
                } else {
                    handleErrors(err);
                }
            } finally {
                setFetching(false);
            }
        };

        fetchTestimonial();

        const $ = window.jQuery;

        $.validator.addMethod('extension', function (value, element, allowedExtensions) {
            if (!value) return true;
            var extension = value.split('.').pop().toLowerCase();
            return $.inArray(extension, allowedExtensions) !== -1;
        }, 'Invalid file type');

        if (formRef.current && $.fn.validate) {
            $(formRef.current).validate({
                rules: {
                    title: { required: true, maxlength: 300 },
                    username: { required: true },
                    img: {
                        required: false,
                        extension: ['jpg', 'jpeg', 'png', 'svg', 'webp']
                    }
                },
                messages: {
                    title: {
                        required: "Please enter title",
                        maxlength: "Title cannot exceed 300 characters"
                    },
                    username: { required: "Please enter user name" },
                    img: { extension: "Only jpg, jpeg, png, svg, webp allowed" }
                },
                ignore: [],
                errorElement: "label",
                validClass: "is-valid",
                errorClass: "is-invalid text-danger",
                highlight: function (element, errorClass, validClass) {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }
    }, [slug]);

    const handleImgChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImg(file);
            setImgPreview(URL.createObjectURL(file));
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.TESTIMONIALS}>Testimonials</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={ROUTES.EDIT_TESTIMONIAL.replace(':slug', slug)}>Edit Testimonial</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Edit Testimonial</h4>
                        </div>
                        <div className="card-body">
                            {fetching ? (
                                <div className="text-center my-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <form ref={formRef} noValidate>
                                    <div className="row">
                                        <div className="mb-3 col-lg-6 col-md-6">
                                            <label className="form-label">Title<sup style={{ color: 'red' }}>*</sup></label>
                                            <input
                                                name="title"
                                                type="text"
                                                className="form-control"
                                                placeholder="Ex. Excellent Service"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                            />
                                        </div>
                                        <div className="mb-3 col-lg-6 col-md-6">
                                            <label className="form-label">User Name<sup style={{ color: 'red' }}>*</sup></label>
                                            <input
                                                name="username"
                                                type="text"
                                                className="form-control"
                                                placeholder="Ex. John Doe"
                                                value={username}
                                                onChange={(e) => setUsername(e.target.value)}
                                            />
                                        </div>
                                        <div className="mb-3 col-lg-6 col-md-6">
                                            <label className="form-label">Designation</label>
                                            <input
                                                name="designation"
                                                type="text"
                                                className="form-control"
                                                placeholder="Ex. CEO"
                                                value={designation}
                                                onChange={(e) => setDesignation(e.target.value)}
                                            />
                                        </div>
                                        <div className="mb-3 col-lg-6 col-md-6">
                                            <label className="form-label">Image</label>
                                            <div className="input-group custom_file_input">
                                                <div className="form-file">
                                                    <input
                                                        name="img"
                                                        type="file"
                                                        className="form-file-input form-control"
                                                        onChange={handleImgChange}
                                                    />
                                                </div>
                                                <span className="input-group-text">Upload</span>
                                            </div>
                                            {imgPreview && (
                                                <div className="mt-2">
                                                    <img
                                                        src={imgPreview}
                                                        alt="Preview"
                                                        style={{ width: 100, height: 100, objectFit: 'contain' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className="mb-3 col-lg-12">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                name="description"
                                                className="form-control"
                                                rows={4}
                                                placeholder="Enter testimonial content..."
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="pt-3">
                                        <button
                                            type="button"
                                            onClick={handleFormSubmit}
                                            className="btn btn-sm btn-primary me-2"
                                            disabled={submitting}
                                        >
                                            {submitting ? (
                                                <>
                                                    <i className="spinner-border spinner-border-sm me-1"></i>
                                                    Submit
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-floppy-disk me-1"></i>
                                                    Submit
                                                </>
                                            )}
                                        </button>
                                        <Link to={ROUTES.TESTIMONIALS} className="btn btn-sm btn-danger light">
                                            <i className="fas fa-times-circle"></i>&nbsp;
                                            Cancel
                                        </Link>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default EditTestimonial;
